import jp from 'jsonpath'
import axios from "axios";
import Icons from "./components/common/icon-selector/IconData";
import { IContextProvider } from "./uxp"
import { ISerializedUIField, IWidgetTemplateProps } from "./components/common/common";
import { extractDataFromFields } from "./components/common/dnd";
import { IContextData } from "./hooks/useContextData";
import { executeAndBind, IDataSource, IDataSourceBinding } from './components/common/data-source-picker/datasource';
import { hasPlaceholders, parsePlaceholders } from 'uxp/components';

export function generateFullUUID() {
    var d = new Date().getTime();
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x7 | 0x8)).toString(16);
    });
    return 'u-' + uuid; // appending a string to prevent from braking the json paths
}

export function generateUUID() {
    return 'u' + Math.random().toString(36).substring(2, 15); // appending a string to prevent from braking the json paths
}

export const buildUrl = (context: IContextProvider, steps: string | string[]): string => {
    let path = ""
    if (typeof steps == 'string') {
        path = steps
    }
    else {
        path = steps.join("/")
    }

    path = path.trim().length > 0 ? "/" + path : path
    return context.lucyUrl + 'Apps/UXP/designer' + path
}

export function isPathValid(data: any, path: string) {
    try {
        let paths = jp.paths(data, path)

        return paths.length > 0

    } catch (e) {
        return false;
    }
}

export function hasProperty(data: any, path: string) {
    return isPathValid(data, path)
}

export function hasValue(value: any, allowZero?: boolean, allowNegative?: boolean) {

    if (allowZero && typeof value == "number" && value == 0) return true
    if (allowNegative && typeof value == "number" && value < 0) return true
    if (!value) return false
    switch (typeof value) {
        case 'string':
            return value?.trim().length > 0
        case 'number':
            return value > 0
        default:
            return true
    }
}

export function tryParseJSON(x: string, def: any = null) {
    try {
        return JSON.parse(x);
    } catch {
        return def;
    }
}

function isScriptExists(id?: string, remove?: boolean) {
    if (!hasValue(id)) return false
    const element = document.getElementById(id)
    if (!element) return false

    if (remove) {
        element.remove()
        return false
    }

    return true
}

export function loadScriptFile(path: string, callback?: any, id?: string, replaceIfExist?: boolean) {

    const scriptExists = isScriptExists(id, replaceIfExist)

    if (scriptExists) {
        if (typeof callback == "function") {
            callback();
        }
    }
    else {
        var script = document.createElement('script');

        script.onload = function () {
            if (typeof callback == "function") {
                callback();
            }
        };

        if (id) {
            script.setAttribute('id', id);
        }
        script.setAttribute('src', path);
        document.head.appendChild(script);
    }
}

export function loadScriptFileAsync(path: string, id?: string, replaceIfExist?: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
        loadScriptFile(path, () => resolve(), id, replaceIfExist);
    });
}


export function containsExpressions(value: string): boolean {
    /**
     * hasPlaceholders is a new function added in experimental version, not avaialbel on other versions yet.
     * so chekc if its available and use it or use the old implementation. 
     * we can remove these once this function is available on all the versions
     */

    // Check if parsePlaceholders is available
    if (typeof hasPlaceholders !== 'undefined') {
        return hasPlaceholders(value);
    }

    // Fallback to original implementation
    const localRegexp = new RegExp(/{\$(.*?)}/); // to prevent keeping status and returning alternating values 
    return localRegexp.test(value);
}

export function parseValue(value: string, data: any): any {
    /**
     * parsePlaceholders is a new function added in experimental version, not avaialbel on other versions yet.
     * so chekc if its available and use it or use the old implementation. 
     * we can remove these once this function is available on all the versions
     */

    // Check if parsePlaceholders is available
    if (typeof parsePlaceholders !== 'undefined') {
        return parsePlaceholders(value, data);
    }

    // Fallback to original implementation
    const expressionRegexp = /{\$(.*?)}/g
    value = ('' + value)?.trim?.() || '';

    // check if the input is a value or expression 
    const hasExpressions = containsExpressions(value)

    if (!hasExpressions) return value

    let expressionCount = (value.match(expressionRegexp) || []).length
    if (expressionCount == 1 && value.startsWith("{$.") && value.endsWith("}")) {
        let expression = '$.' + (value.replace("{$.", '').replace("{$", '').replace("}", '')).trim()
        if (!isPathValid(data, expression)) return ''

        let parsedValue = jp.value(data, expression)
        return parsedValue
    }

    // get expression 
    const returnValue = value.replace(expressionRegexp, (match, contents) => {

        let stringContent: string = contents as string
        if (stringContent.trim().length == 0) return match

        // get expression 
        let expression = `$` + stringContent.trim()

        if (!isPathValid(data, expression)) return ''
        let parsedValue = jp.value(data, expression)
        if (typeof parsedValue == 'object') {
            return JSON.stringify(parsedValue)
        }
        return parsedValue
    })

    return returnValue
}
export function convertColor(s: string, contextData?: any) {
    if (!contextData) return s;
    return parseValue(s, contextData);
}
export function convertColorStyles(styles: any, contextData?: any, defaults?: any) {
    if (!contextData) return styles;
    let newStyles: any = {};
    for (let s of ['backgroundColor', 'color']) {
        if (hasValue(styles[s])) {
            newStyles[s] = convertColor(styles[s], contextData);
        } else {
            if (hasValue(defaults?.[s])) {
                newStyles[s] = convertColor(defaults[s], contextData);
            }
        }
    }
    return { ...styles, ...newStyles };
}
export type DebounceFunction = (...args: any[]) => void;
export type DebounceOptions = { isImmediate: boolean }

export function debounce<F extends DebounceFunction>(
    func: F,
    waitMilliseconds = 50,
    options: DebounceOptions = {
        isImmediate: false
    },
): F {
    let timeoutId: any = undefined;

    return function (this: any, ...args: any[]) {
        const context = this;

        const doLater = function () {
            timeoutId = undefined;
            if (!options.isImmediate) {
                func.apply(context, args);
            }
        }

        const shouldCallNow = options.isImmediate && timeoutId === undefined;

        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(doLater, waitMilliseconds);

        if (shouldCallNow) {
            func.apply(context, args);
        }
    } as any
}

export function getQueryVariable(key: string) {
    var query = window.location.search.substring(1);
    var vars = query.split("&");
    for (var i = 0; i < vars.length; i++) {
        var pair = vars[i].split("=");
        if (pair[0] == key) { return pair[1]; }
    }
    return '';
}

// Extract the json path from json schema
export function extractPathFromSchema(schema: any, path: string) {
    try {
        if (!path) return schema;
        let subSchema = schema
        path = path.trim()
        let elements = jp.parse(path)

        for (let i = 0; i < elements.length; i++) {
            let el = elements[i]
            if (el?.expression?.type === "root") {
                subSchema = schema;
            }

            if (el?.expression?.type === "identifier") {
                // Objects
                subSchema = schema?.properties?.[el?.expression?.value]
            }

            if (el?.operation === "subscript" || el?.expression?.type === "numeric_literal") {
                // Arrays
                subSchema = schema?.items
            }
        }

        return subSchema
    } catch (e) {
        console.error(e)
        return schema;
    }

    // Only direct children are supported, cannot support descendant items like $..abc
}

export function bindContainerId(id: string, containerId?: string) {
    if (hasValue(containerId)) return containerId + '-' + id
    else return id
}
export function formatNumber(n: number | string, decimals?: number) {
    let x = Number(n);
    if (isNaN(x)) return '' + n;
    if (!decimals) {
        decimals = 2;
        if (x > 99) {
            decimals = 1;
        }
        if (x > 999) {
            decimals = 0;
        }
    }
    x = Number(x.toFixed(decimals));
    return Intl.NumberFormat().format(x);
}

export function isPromise(p: any) {
    return (typeof p === 'object' && p?.then && typeof p.then == 'function')
}

export function isNone(v: any) {
    return (v === null || v === undefined)
}


export async function isComponentEnabled(context: IContextProvider, component: string) {
    try {
        let config: any = {
            url: `/components/is-enabled/${component}`,
            method: 'get',
            headers: {
                'Authorization': `APIKEY ${context.apiKey}`,
                'Content-Type': 'application/json',
            }
        }
        let resp = await axios(config)
        return resp.data
    }
    catch (e) {
        let obj = e?.response?.data || e;
        throw obj;
    }
}

// type ServiceMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';
// export async function executeComponent(context: IContextProvider, component: string, route: string, method: ServiceMethod, parameters: any, body?: any) {
//     if (!route.startsWith('/')) route = '/' + route;
//     try {

//         let config: any = {
//             url: `/components/${component}${route}`,
//             method: method,
//             headers: {
//                 'Authorization': `APIKEY ${context.apiKey}`,
//                 'Content-Type': 'application/json',
//             }
//         }

//         if (['patch', 'post', 'put'].includes(method)) {
//             config.data = body || {}
//         }
//         config.params = parameters

//         let resp = await axios(config)
//         return resp.data
//     } catch (e) {
//         let obj = e?.response?.data || e;
//         throw obj;
//     }
// }

// Not to make breaking changes to the previously designed widgets
function searchForFieldNameInDataSources(data: { [datasourceID: string]: any }, fieldName: string) {
    if (typeof data !== 'object') {
        return null;
    }

    try {
        // Go through each data source and find value
        for (const d in data) {
            let dataFromEachDataSource = data[d]

            if (dataFromEachDataSource?.hasOwnProperty(fieldName)) {
                return dataFromEachDataSource[fieldName];
            }
        }
    } catch (e) {
        console.error(e)
        return null
    }
}

export function replaceRegExp() {

}

export function replaceWithData(text: string, data: IContextData) {
    // text = text.trim()
    // TODO: Add widget parameters
    let hasExpression = /{\$(.*?)}/g.test(text || '')

    if (hasExpression) {
        return parseValue(text.trim(), data)
    }

    let v = searchForFieldNameInDataSources(data, text)
    if (v) return v;

    return text;
}

export const getUrlFriendlyString = (string: string, removeSlashes?: boolean): string => {
    const from = "ãàáäâẽèéëêìíïîõòóöôùúüûñç·/_,:;"
    const to = "aaaaaeeeeeiiiiooooouuuunc------"

    const newText = string.split('').map(
        (letter, i) => letter.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i)))

    return newText
        .toString()                     // Cast to string
        .toLowerCase()                  // Convert the string to lowercase letters
        .trim()                         // Remove whitespace from both sides of a string
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/'/g, '-e')           // Replace single quates with -
        .replace(/&/g, '-and-')           // Replace & with 'and'
        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
        .replace(/\-\-+/g, '-');        // Replace multiple - with single -
}

export async function loadInitialData(props: IWidgetTemplateProps): Promise<{ isLoading: boolean, initialData: any }> {

    const datasources = (props?.uiProps?.datasources || []) as IDataSource[]
    const bindings = (props?.uiProps?.initialSources || []) as IDataSourceBinding[]

    const initialData: { [sourceIs: string]: any } = {}

    await Promise.all(bindings.map(async b => {
        try {
            const data = await executeAndBind(props.uxpContext, b, datasources, {}, {}, {})
            console.log('initialdata__', data)
            initialData[b.source] = data
        } catch (e) {
            console.log('initialdata__error', e)

        }
    }))

    // TODO :: implement loading initial data 
    return { isLoading: false, initialData: initialData }
}

export async function extractContextData(props: IWidgetTemplateProps, initialData: any, currentContextData: any) {
    /**
       * props contains all the props we passed to a widget 
       * params   - any parameters 
       * uiProps  - widget configuration 
       *              - this will have filters and forms 
       * 
       */

    let data: { [key: string]: any } = { ...currentContextData }

    // data sources 
    // initial data should have the following structure 
    // {[sourceId: string]: any  }
    // to use these can use the following expression {$.datasource.<source id >.<field name>}
    data.datasource = initialData

    // params passed down to the widget 
    data.param = props.params || {}

    if (props?.uiProps?.fields) {
        // get form data 
        let fields: ISerializedUIField[] = props?.uiProps?.fields || []
        data = await extractDataFromFields(props, data, fields)
    }

    console.log('_____checking filters ', props?.uiProps)
    if (props?.uiProps?.filter?.fields) {
        // get filter data
        let filterFields: ISerializedUIField[] = props?.uiProps?.filter?.fields || []
        data = await extractDataFromFields(props, data, filterFields, 'filters')
    }
    return data
}

export function updateParentUIPropsWithDefaultWidgetUIProps(parentUIProps: any, uiProps: any, index: number) {
    if (!!parentUIProps) {
        let _parentProps = { ...parentUIProps }
        _parentProps.fields[index].props.uiProps = uiProps
        return _parentProps
    }
    else return uiProps
}

export function getRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function getRandomIcon() {
    const iconNames = Object.keys(Icons)
    return iconNames[getRandomInt(0, iconNames.length)]
}

export async function wait(time: number) {

    return new Promise<any>((done, nope) => {
        setTimeout(() => {
            done('time is up')
        }, time);
    })
}

export function toStr(value: any, def?: string) {
    if (!value) return def || ''
    const _type = typeof value

    if (_type == 'string') return value

    switch (_type) {
        case 'object':
            return JSON.stringify(value)
        case 'boolean':
            return value ? '1' : '0'
        default:
            return value?.toString()
    }
}

export function toBool(value: any, def?: boolean) {
    const _type = typeof value
    if (_type == 'boolean') return value

    if (_type == 'string') {
        return value?.toLowerCase() == 'true'
    }

    if (_type == 'number') return value == 1
    return def || false
}

export function toNum(value: any, def?: number) {
    const _type = typeof value
    if (_type == 'number') return value

    if (_type == 'string') {
        const _n = Number(value)
        if (isNaN(_n) || !isFinite(_n)) return def || 0
        return _n
    }
    return def || 0
}

export function toJSON(value: any, def?: Array<any> | { [key: string]: any }) {
    const _type = typeof value
    if (_type == 'object') return value

    if (_type == 'string') {
        return tryParseJSON(value, def || {})
    }
    return def || {}
}